import React, { useRef, useState, useMemo } from "react";

import { browserUtils } from "@local-logic/core/utils";
import { textWithHighlighting } from "~/utils/formatters/textWithHighlighting";

import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { Form } from "@local-logic/design-system";

import type { TravelMode } from "~/sdks/LocalContent/hooks/useCommute/types";

import {
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "~/sdks/LocalContent/types";

import { useTracking } from "@local-logic/core/tracking";
import { useTranslation } from "react-i18next";
import { useAddressSearch } from "~/sdks/LocalContent/hooks/useAddressSearch";
import { useRootElement } from "../context";

import { Header } from "./Header";
import { Route } from "./Route";
import { NoRoute } from "./NoRoute";
import { Select } from "./Select";

import * as styles from "./styles";

export function Content() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("localContent");
  const tracking = useTracking();
  const {
    isOpen,
    commuteData,
    commuteOptions,
    isCommuteDataLoading,
    handleCalculateCommute,
  } = useRootElement();

  // State for search input field
  const [isFieldFocused, setIsFieldFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedValue, setSelectedValue] = useState<string | undefined>();

  const { data, isLoading } = useAddressSearch({ query: searchQuery });

  const suggestionResults = useMemo(
    () =>
      !searchQuery
        ? browserUtils.commuteHistory.getSuggestionHistory()
        : data?.suggestions || [],
    [searchQuery, data?.suggestions]
  );

  const options = useMemo(() => {
    if (!isFieldFocused || !suggestionResults.length) {
      return [];
    }

    return suggestionResults
      .filter(
        (suggestion) => typeof suggestion?.address?.description === "string"
      )
      .map((suggestion) => ({
        value: suggestion.address?.description || "",
        label: textWithHighlighting(
          suggestion.address?.description || "",
          searchQuery
        ),
        key: suggestion.locationId || `address-${suggestion.locationId}`,
      })) as unknown as Form.Option<string>[];
  }, [isFieldFocused, searchQuery, suggestionResults]);

  const handleOnSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleOnSearchClear = () => {
    handleCalculateCommute({
      ...commuteOptions,
      label: undefined,
      destination: undefined,
    });
  };

  const handleOnSelect = (value: string) => {
    setSelectedValue(value);

    const selectedSuggestion = suggestionResults.find(
      (suggestion) => suggestion.address?.description === value
    );

    if (typeof selectedSuggestion?.address?.coordinates !== "undefined") {
      browserUtils.commuteHistory.push(selectedSuggestion);
      handleCalculateCommute({
        ...commuteOptions,
        label: selectedValue,
        destination: {
          lat: selectedSuggestion.address.coordinates.latitude,
          lng: selectedSuggestion.address.coordinates.longitude,
        },
      });

      const eventAttributes = {
        label: value,
        mode: commuteOptions.commuteType,
        destinationLat: selectedSuggestion.address.coordinates.latitude,
        destinationLng: selectedSuggestion.address.coordinates.longitude,
      };

      tracking.postEvent({
        eventType: TrackingPipelineEvents.COMMUTE_CALCULATOR_CALCULATE_ROUTE,
        eventAttributes: Object.entries(eventAttributes).map(
          ([key, objectValue]) => ({
            label: key,
            data: objectValue,
          })
        ),
      });

      tracking.trackAnalyticsEvent({
        eventName: AnalyticsEvents.COMMUTE_CALCULATOR_CALCULATE_ROUTE,
        eventAttributes,
      });
    }
  };

  const handleOnFieldFocus = () => {
    setIsFieldFocused(true);
  };

  const handleOnFieldBlur = () => {
    setIsFieldFocused(false);
  };

  const handleOnCommuteTypeChange = (value: TravelMode) => {
    handleCalculateCommute({
      ...commuteOptions,
      commuteType: value,
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <Header />

      <div className={styles.route}>
        <div className={styles.form}>
          <Form.Root initialValues={{ searchQuery: "" }}>
            <Form.SearchAndSelect<string>
              name="searchQuery"
              label={
                !selectedValue &&
                !isFieldFocused &&
                typeof commuteData?.label === "string"
                  ? commuteData.label
                  : t("commute.placeholder")
              }
              clearOnSelect={false}
              selectedLabel={selectedValue}
              prefixIcon={MagnifyingGlassIcon}
              isLoading={isLoading || isCommuteDataLoading}
              options={options}
              onChange={handleOnSearchChange}
              onClear={handleOnSearchClear}
              onSelect={handleOnSelect}
              onFocus={handleOnFieldFocus}
              onBlur={handleOnFieldBlur}
            />
          </Form.Root>

          <Select
            name="commuteType"
            selected={commuteOptions.commuteType}
            onChange={handleOnCommuteTypeChange}
          />
        </div>

        <Route isLoading={isCommuteDataLoading} />
        {typeof commuteOptions.destination !== "undefined" &&
          commuteData === null &&
          !isCommuteDataLoading && <NoRoute />}
      </div>
    </div>
  );
}
